import { createFileRoute } from "@tanstack/react-router";
import { ogImageMeta } from "@/lib/og-images";
import { WORK } from "@/lib/work";
import { TalkToJonButton } from "@/components/talk-to-jon";
import { SiteFooter } from "@/components/site-footer";
import { WorkTile } from "@/components/work/work-tile";

export const Route = createFileRoute("/work/")({
  head: () => ({
    meta: [
      { title: "Our Work · Products We Designed and Built | Vibing Co" },
      {
        name: "description",
        content:
          "Real products, live in production: a fashion platform, a moodboard tool, a booking system, file tools, a status dashboard and more. Designed and built end to end by full-stack designers.",
      },
      { property: "og:title", content: "Our work · Products we designed and shipped" },
      ...ogImageMeta("services", "Share card: product studio case studies"),
      {
        property: "og:description",
        content:
          "Eight products we designed, built and run in production, shown as they are today.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://vibing.inc/work" },
    ],
    links: [{ rel: "canonical", href: "https://vibing.inc/work" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: "Products designed and built by the Vibing Company",
          itemListElement: WORK.map((w, i) => ({
            "@type": "ListItem",
            position: i + 1,
            url: `https://vibing.inc/work/${w.slug}`,
            name: `${w.name}: ${w.title}`,
          })),
        }),
      },
    ],
  }),
  component: WorkIndex,
});

function WorkIndex() {
  const [first, ...middle] = WORK;
  const last = middle.length % 2 === 1 ? middle.pop() : undefined;

  return (
    <div className="min-h-dvh bg-background text-foreground">
      <main id="main" tabIndex={-1}>
        <section className="mx-auto max-w-[1400px] px-6 pb-14 pt-[clamp(9rem,18vh,13rem)] md:px-10 md:pb-20">
          <h1
            className="max-w-[18ch] font-display leading-[1.02] tracking-[-0.035em]"
            style={{ fontSize: "clamp(2rem, 4.4vw, 3.5rem)" }}
          >
            Products we designed and shipped.
          </h1>
          <p className="mt-8 max-w-[56ch] text-pretty text-xl leading-snug text-muted-foreground md:text-2xl">
            Every screenshot on this page is the live product, as it looks today.
          </p>
          <div className="mt-10">
            <TalkToJonButton variant="dark" immersive />
          </div>
        </section>

        <section aria-label="Case studies" className="mx-auto max-w-[1400px] px-6 pb-20 md:px-10 md:pb-32">
          <div className="grid gap-5 md:grid-cols-2">
            <div className="md:col-span-2">
              <WorkTile item={first} lead eager index={0} />
            </div>
            {middle.map((w, i) => (
              <WorkTile key={w.slug} item={w} index={i + 1} />
            ))}
            {last ? (
              <div className="md:col-span-2">
                <WorkTile item={last} lead index={middle.length + 1} />
              </div>
            ) : null}
          </div>
        </section>

        <section className="border-t border-border/60 bg-foreground text-background">
          <div className="mx-auto max-w-[1400px] px-6 py-20 md:px-10 md:py-32">
            <h2
              className="max-w-[18ch] font-display leading-[1.02] tracking-[-0.035em]"
              style={{ fontSize: "clamp(2rem, 4.4vw, 3.5rem)" }}
            >
              Yours could be next.
            </h2>
            <p className="mt-8 max-w-[48ch] text-lg leading-snug text-background/70 md:text-xl">
              Bring the idea or the half-built version. We will tell you what 14 days can do.
            </p>
            <div className="mt-10">
              <TalkToJonButton variant="light" />
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
